import { useCallback, useState } from 'react';
import { localDateISO } from '@/shared/lib/dates';
import { useHouseholdContext } from '@/shared/lib/household';
import { useCompleteTaskAction } from '@/features/complete-task';
import { useRedoTaskAction } from '@/features/redo-task';
import { useUncompleteTaskAction } from '@/features/uncomplete-task';

/** A mission card as the actions address it (ADR-0142): its optimistic id + natural key. */
export interface MissionCard {
  readonly id: string;
  readonly templateId: string;
  readonly scheduledLocalDate: string;
  readonly assigneeId: string;
}

/**
 * The three optimistic mission controls (complete / redo / undo) bundled for a shelf/digest surface
 * (ADR-0141). A mission has no calendar day, so we key the underlying day-scoped actions on TODAY — the
 * hooks reconcile the date-independent Missions cache (`familyKeys.missions`) regardless, and the
 * harmless today-day invalidation costs nothing. Complete/undo are addressed by the card's natural key
 * `(templateId, localDate)` (ADR-0142); redo still targets a real rejected occurrence by id.
 */
export function useMissionActions(assigneeId: string): {
  complete: (mission: MissionCard) => void;
  redo: (occurrenceId: string) => void;
  uncomplete: (mission: MissionCard) => void;
  isCompleting: (occurrenceId: string) => boolean;
  isRedoing: (occurrenceId: string) => boolean;
  isUndoing: (occurrenceId: string) => boolean;
  /** The MOST RECENTLY invoked action's failure (post optimistic rollback), or null — for a banner. */
  error: unknown;
} {
  const { timeZone } = useHouseholdContext();
  const today = localDateISO(timeZone);
  const {
    complete,
    isOccurrencePending: isCompleting,
    error: completeError,
  } = useCompleteTaskAction({
    date: today,
    assigneeId,
    childId: assigneeId,
  });
  const {
    redo,
    isOccurrencePending: isRedoing,
    error: redoError,
  } = useRedoTaskAction({ date: today, assigneeId });
  const {
    uncomplete,
    isOccurrencePending: isUndoing,
    error: undoError,
  } = useUncompleteTaskAction({
    date: today,
    assigneeId,
    childId: assigneeId,
  });
  // The three errors are independent mutations' state — none resets the OTHERS' error, so a stale
  // failure from one action could mask a fresh SUCCESS on another. Track which action fired LAST and
  // surface ONLY that one's error (same rationale as TodayPage's day-board banner).
  const [lastAction, setLastAction] = useState<'complete' | 'redo' | 'undo' | null>(null);

  const completeTracked = useCallback(
    (mission: MissionCard) => {
      setLastAction('complete');
      complete(mission);
    },
    [complete],
  );
  const redoTracked = useCallback(
    (occurrenceId: string) => {
      setLastAction('redo');
      redo(occurrenceId);
    },
    [redo],
  );
  const uncompleteTracked = useCallback(
    (mission: MissionCard) => {
      setLastAction('undo');
      uncomplete(mission);
    },
    [uncomplete],
  );

  const error =
    lastAction === 'complete'
      ? completeError
      : lastAction === 'redo'
        ? redoError
        : lastAction === 'undo'
          ? undoError
          : null;

  return {
    complete: completeTracked,
    redo: redoTracked,
    uncomplete: uncompleteTracked,
    isCompleting,
    isRedoing,
    isUndoing,
    error,
  };
}
